/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import styled, { css } from 'styled-components';

const DEFAULT_POS = -6;
const DEFAULT_SIZE = 30;

const Wrapper = styled.div<{ $animateTo?: number; $size: number }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0 !important;
  pointer-events: none;

  > span:before {
    content: '';
    display: block;
    position: absolute;
    width: ${({ $size }) => `${$size}px`};
    height: ${({ $size }) => `${$size}px`};
    transition:
      transform ${({ theme }) => theme.animation.DURATION}
        ${({ theme }) => theme.animation.EASING},
      opacity ${({ theme }) => theme.animation.DURATION}
        ${({ theme }) => theme.animation.EASING};
  }

  ${({ $animateTo }) =>
    $animateTo &&
    css`
      opacity: 0;
      pointer-events: all;

      &:hover {
        opacity: 1;

        ${TopLeft}::before {
          transform: translate(-${$animateTo}px, -${$animateTo}px);
        }

        ${BottomLeft}::before {
          transform: translate(-${$animateTo}px, ${$animateTo}px);
        }

        ${TopRight}::before {
          transform: translate(${$animateTo}px, -${$animateTo}px);
        }

        ${BottomRight}::before {
          transform: translate(${$animateTo}px, ${$animateTo}px);
        }
      }
    `}

  [data-theme='pink'] & {
    --button-cornerline-color: ${({ theme }) => theme.paletteCssVars.fg1200};
  }
`;

const TopLeft = styled.span<{ $position: number }>`
  &:before {
    border-top: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    border-left: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    top: ${({ $position }) => `${$position}px`};
    left: ${({ $position }) => `${$position}px`};
  }
`;

const TopRight = styled.span<{ $position: number }>`
  &:before {
    border-top: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    border-right: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    top: ${({ $position }) => `${$position}px`};
    right: ${({ $position }) => `${$position}px`};
  }
`;

const BottomLeft = styled.span<{ $position: number }>`
  &:before {
    border-bottom: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    border-left: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    bottom: ${({ $position }) => `${$position}px`};
    left: ${({ $position }) => `${$position}px`};
  }
`;

const BottomRight = styled.span<{ $position: number }>`
  &:before {
    border-bottom: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    border-right: 1px solid
      ${({ theme }) =>
        `var(--button-cornerline-color, ${theme.borderColor.DEFAULT})`};
    bottom: ${({ $position }) => `${$position}px`};
    right: ${({ $position }) => `${$position}px`};
  }
`;

interface CornerLinesProps {
  animateTo?: number;
  bottomLeft?: boolean;
  bottomRight?: boolean;
  interactive?: boolean;
  position?: number;
  size?: number;
  topLeft?: boolean;
  topRight?: boolean;
}

export const CornerLines: React.ComponentType<CornerLinesProps> = React.memo(
  ({
    topLeft,
    topRight,
    bottomLeft,
    bottomRight,
    position = DEFAULT_POS,
    size = DEFAULT_SIZE,
    animateTo
  }) => {
    return (
      <Wrapper $animateTo={animateTo} $size={size}>
        {topLeft && <TopLeft $position={position} />}
        {topRight && <TopRight $position={position} />}
        {bottomLeft && <BottomLeft $position={position} />}
        {bottomRight && <BottomRight $position={position} />}
      </Wrapper>
    );
  }
);
